import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Check, Instagram, Mail, ArrowRight, Handshake } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERKS = (kit) => [
  `Official partner credit on the ${kit.city} tee page — "Partnered with ${kit.club.name}" with a tap-through to your Instagram`,
  'This co-branded 1080×1080 launch graphic is yours to post — we post it too, same day',
  'Ready-made captions for both accounts, so launch day takes 5 minutes',
  "Your crew in front of NinjAthlete's HYROX + running audience. Zero cost to you",
];

export default function PartnerKitPage() {
  const { cityKey } = useParams();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/city-series/partner-kit/${cityKey}`)
      .then((r) => { if (!r.ok) throw new Error('404'); return r.json(); })
      .then(setKit)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [cityKey]);

  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
      </main>
    );
  }

  if (notFound || !kit) {
    return (
      <main className="pt-20 min-h-screen">
        <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="partner-kit-not-found">
          <h1 className="text-white text-4xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Kit Not Found</h1>
          <p className="text-zinc-400 text-sm">This link may be outdated — DM @ninjathlete and we'll send a fresh one.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen" data-testid="partner-kit-page">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <p className="flex items-center gap-2 text-[#C7FA3E] text-xs uppercase tracking-[0.25em] mb-3" style={{ fontFamily: 'Oswald, sans-serif' }}>
          <Handshake className="w-3.5 h-3.5" /> Partner Launch Kit · City Series Drop 01
        </p>
        <h1 className="text-white text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.02em' }} data-testid="partner-kit-headline">
          NinjAthlete × {kit.club.name}
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          We designed a limited <strong className="text-zinc-200">{kit.city}</strong> tee — <strong className="text-zinc-200">"{kit.design}"</strong> ({kit.tagline}) —
          and we want <strong className="text-zinc-200">{kit.club.name}</strong> to be the official partner club on it.
          Here's exactly what the collab looks like:
        </p>

        <img
          src={`${API_URL}${kit.image_path}`}
          alt={`NinjAthlete × ${kit.club.name} launch graphic`}
          className="w-full max-w-md mx-auto border border-zinc-800 mb-3"
          data-testid="partner-kit-graphic"
        />
        <p className="text-zinc-600 text-[11px] text-center mb-10">Your co-branded launch post — ready the moment you say yes.</p>

        <div className="border border-zinc-800 bg-zinc-900/50 p-6 mb-8" data-testid="partner-kit-perks">
          <h2 className="text-white text-lg mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.03em' }}>The Deal</h2>
          <ul className="space-y-3">
            {PERKS(kit).map((p, i) => (
              <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm leading-relaxed">
                <Check className="w-4 h-4 text-[#C7FA3E] flex-shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-zinc-800 bg-zinc-900/50 p-6 mb-8 flex flex-wrap items-center justify-between gap-4" data-testid="partner-kit-tee">
          <div>
            <p className="text-white text-sm font-semibold">{kit.city} — "{kit.design}" Tee</p>
            <p className="text-zinc-500 text-xs mt-1">{kit.tagline} · Limited run · ${kit.price.toFixed(0)}</p>
          </div>
          <Link
            to="/city-series"
            className="inline-flex items-center gap-1.5 text-[#C7FA3E] text-xs uppercase tracking-widest hover:gap-2.5 transition-all"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="partner-kit-view-drop"
          >
            See the full drop <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="border border-[#C7FA3E]/40 bg-[#C7FA3E]/[0.06] p-6" data-testid="partner-kit-cta">
          <p className="text-white text-sm font-semibold mb-1">In? It takes one reply.</p>
          <p className="text-zinc-400 text-xs leading-relaxed mb-4">
            Reply to our DM on Instagram or shoot us an email and we'll lock in {kit.club.name} as the official {kit.city} partner — credit goes live the same day.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://www.instagram.com/ninjathlete/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#C7FA3E] hover:bg-[#b3e62e] text-black text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="partner-kit-dm-btn"
            >
              <Instagram className="w-4 h-4" /> DM @ninjathlete
            </a>
            <a
              href="mailto:hello@ninjathlete.com?subject=City%20Series%20Partnership"
              className="inline-flex items-center gap-2 px-6 py-2.5 border border-zinc-700 text-zinc-300 hover:border-[#C7FA3E] hover:text-[#C7FA3E] text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ fontFamily: 'Oswald, sans-serif' }}
              data-testid="partner-kit-email-btn"
            >
              <Mail className="w-4 h-4" /> hello@ninjathlete.com
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
